import React, { useMemo } from 'react';
import { useDashboardVariant } from '../../data/domains/config';
import { useRtl } from '../../shared/i18n/useRtl';
import type { ThemeColors } from '../../shared/theme';
import {
  getActiveMealDayProgressFromSubscription,
  type DayConfig,
  type SubscriptionData,
  type DateServiceConfig,
} from '../../data/domains/subscription';
import { DashboardSubscriptionCardSet1 } from './variants/set1/DashboardSubscriptionCardSet1';
import { DashboardSubscriptionCardSet2 } from './variants/set2/DashboardSubscriptionCardSet2';
import { DashboardCardShellSet2 } from './variants/set2/DashboardCardShellSet2';
import { DashboardSubscriptionCardSet3 } from './variants/set3/DashboardSubscriptionCardSet3';
import type { FlatItem } from './DashboardMenuImagesStrip';

export type DashboardSubscriptionCardProps = {
  colors: ThemeColors;
  subscription: SubscriptionData | null;
  available_count: number;
  available_dates: string[];
  active_subscriptions: SubscriptionData[];
  day_config_map: Record<string, DayConfig>;
  date_service_config: DateServiceConfig | null;
  subscription_loading: boolean;
  on_press: () => void;
  rtl: ReturnType<typeof useRtl>;
  /** Fallback meal images for set1/set3 when subscription has no photo. */
  menu_fallback_items?: FlatItem[];
};

export function DashboardSubscriptionCard({
  colors,
  subscription,
  available_count: _available_count,
  available_dates,
  active_subscriptions,
  day_config_map,
  date_service_config,
  subscription_loading,
  on_press,
  rtl: _rtl,
  menu_fallback_items = [],
}: DashboardSubscriptionCardProps) {
  const variant = useDashboardVariant();

  const meal_progress = useMemo(() => {
    if (!subscription) {
      return { daysLeft: 0, pausedDaysLeft: 0 };
    }
    return getActiveMealDayProgressFromSubscription(
      subscription,
      available_dates,
      active_subscriptions,
      day_config_map,
      date_service_config,
    );
  }, [subscription, available_dates, active_subscriptions, day_config_map, date_service_config]);
  const days_left = meal_progress.daysLeft;
  const paused_days_left = meal_progress.pausedDaysLeft;

  if (variant === 'set1') {
    return (
      <DashboardSubscriptionCardSet1
        colors={colors}
        subscription={subscription}
        subscription_loading={subscription_loading}
        days_left={days_left}
        paused_days_left={paused_days_left}
        menu_fallback_items={menu_fallback_items}
        on_press={on_press}
      />
    );
  }

  if (variant === 'set3') {
    return (
      <DashboardSubscriptionCardSet3
        colors={colors}
        subscription={subscription}
        subscription_loading={subscription_loading}
        days_left={days_left}
        paused_days_left={paused_days_left}
        menu_fallback_items={menu_fallback_items}
        on_press={on_press}
      />
    );
  }

  if (variant === 'set2') {
    return (
      <DashboardCardShellSet2 colors={colors} no_padding>
        <DashboardSubscriptionCardSet2
          colors={colors}
          subscription={subscription}
          subscription_loading={subscription_loading}
          days_left={days_left}
          paused_days_left={paused_days_left}
          on_press={on_press}
          today_menu_strip_items={menu_fallback_items}
        />
      </DashboardCardShellSet2>
    );
  }

  return null;
}
